import {
  ToolApprovalPrefsRequestConfig,
  UpdateToolApprovalPrefsRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { isServer } from "solid-js/web";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { authTokenReady } from "~/wire/authTokenReady";

export function useToolApprovalPrefs() {
  const wire = useWire();
  const client = useStorytellClient();
  const queryClient = useQueryClient();

  const fetchPrefs = async (): Promise<string[]> => {
    const res = await client.fetch(
      ToolApprovalPrefsRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined
    );
    if (res.isErr()) throw res.error;
    return res.value.approvedOperations ?? [];
  };

  const query = useQuery(() => ({
    queryKey: ["tool-approval-prefs"],
    enabled: !isServer && authTokenReady(wire.auth),
    staleTime: 60_000,
    queryFn: fetchPrefs,
  }));

  const mutation = useMutation(() => ({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }): Promise<void> => {
      // The PUT replaces the whole set, and Slack, the desktop app and an
      // approve_always card add to it behind this cache; building from a stale
      // copy would drop what they added.
      const current = await queryClient.fetchQuery({
        queryKey: ["tool-approval-prefs"],
        queryFn: fetchPrefs,
        staleTime: 0,
      });
      const next = enabled ? [...current.filter((k) => k !== key), key] : current.filter((k) => k !== key);
      const res = await client.fetch(
        UpdateToolApprovalPrefsRequestConfig.withBearerToken(wire.auth.token()),
        undefined,
        {
          approvedOperations: next,
        }
      );
      if (res.isErr()) throw res.error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["tool-approval-prefs"] }),
  }));

  const toggle = (key: string, enabled: boolean) => {
    trackFeature({ feature: "integration.auto_approve", via: "button" });
    mutation.mutate({ key, enabled });
  };

  return {
    // Reading data before the first answer suspends the nearest boundary, and the
    // command palette's tools view has none of its own.
    prefs: () => (query.isSuccess ? query.data : undefined),
    isLoading: () => query.isLoading,
    isUpdating: () => mutation.isPending,
    toggle,
  };
}
